import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WorkflowRunCase } from "./useWorkflowRunInstance";
import { fromPifSavedValues, toPifValuesByKey } from "../features/workflows/pifCaseValues";
import { PIF_LOOKUP_FLOWS, PIF_STAGE_KEYS } from "../features/workflows/pifWorkflowKeys";
import {
  PifIdentityLookupError,
  PifIdentityLookupOutcome,
  PifIdentityMatch,
  stageKeyOf,
} from "../features/workflows/workflowRunData";

interface PifResumedIdentityOptions {
  runCase: WorkflowRunCase | null;
  /** The PIF on a real case, with no identity resolved in this session (a refresh, or a case opened
   *  from Instances / My Work). */
  enabled: boolean;
  lookupIdentity: (value: string) => Promise<PifIdentityLookupOutcome>;
}

export interface PifResumedIdentity {
  identity: PifIdentityMatch | null;
  /** The name is still being looked up. pif-3 copies the identity once, when it first shows, so
   *  it waits for this rather than showing blank locked names. */
  resolving: boolean;
  /** Why the re-check by Employee ID failed — explained the same way pif-1 explains it (P3). */
  lookupError: PifIdentityLookupError | null;
  /** Asks again (a 429 or an unreachable directory; a 403 would only fail again). */
  retry: () => void;
}

/**
 * The identity a PIF case past pif-1 was routed on, so a refresh lands back on its screens with
 * nothing lost (AC11). It is rebuilt from the case's saved values. An existing employee's or
 * rehire's name is only saved once pif-3 has saved, so until then it is looked up again ONCE per
 * case by the saved Employee ID (never an SSN), taking only the name and contact from the answer:
 * the case keeps the route it was saved on. A failed lookup leaves the name blank and reports why
 * (403 / 429 / unreachable), as pif-1 does (P3, Pooja on 80430, 2026-10-08); Retry asks again.
 */
const useWorkflowPifResumedIdentity = ({
  runCase,
  enabled,
  lookupIdentity,
}: PifResumedIdentityOptions): PifResumedIdentity => {
  const saved = useMemo(() => {
    if (!enabled || !runCase) return null;
    const { canvases } = runCase.definition;
    const start = canvases.find((stage) => stageKeyOf(stage) === PIF_STAGE_KEYS.start);
    // Still on pif-1: nothing has been routed yet, and pif-1 asks afresh.
    if (!start || runCase.currentStageId === start.id) return null;
    return fromPifSavedValues(toPifValuesByKey(canvases, runCase.values));
  }, [enabled, runCase]);

  // The lookup's answer, for the Employee ID it was asked about (null match = it didn't match).
  const [settled, setSettled] = useState<{
    employeeId: string;
    match: PifIdentityMatch | null;
    error: PifIdentityLookupError | null;
  } | null>(null);
  const askedForRef = useRef<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const employeeId = saved?.employeeId ?? "";
  const needsName = Boolean(saved && !saved.name && employeeId && saved.flow !== PIF_LOOKUP_FLOWS.new);

  // Not cancelled on cleanup: the ref makes this a single lookup per Employee ID and attempt, and an
  // answer is only used for the Employee ID it was asked about.
  useEffect(() => {
    const askedFor = `${employeeId}#${attempt}`;
    if (!needsName || askedForRef.current === askedFor) return;
    askedForRef.current = askedFor;
    void lookupIdentity(employeeId).then((outcome) => {
      setSettled(
        outcome.kind === "matched"
          ? { employeeId, match: outcome.match, error: null }
          : { employeeId, match: null, error: outcome.kind },
      );
    });
  }, [needsName, employeeId, attempt, lookupIdentity]);

  const retry = useCallback(() => {
    setSettled(null);
    setAttempt((current) => current + 1);
  }, []);

  return useMemo(() => {
    if (!saved || !needsName) return { identity: saved, resolving: false, lookupError: null, retry };
    if (settled?.employeeId !== employeeId) return { identity: saved, resolving: true, lookupError: null, retry };
    const { match, error } = settled;
    if (!match) return { identity: saved, resolving: false, lookupError: error, retry };
    return {
      lookupError: null,
      retry,
      identity: {
        ...saved,
        name: match.name,
        firstName: match.firstName,
        middleName: match.middleName,
        lastName: match.lastName,
        phone: saved.phone ?? match.phone,
        email: saved.email ?? match.email,
        department: match.department,
        dateOfBirth: saved.dateOfBirth || match.dateOfBirth,
      },
      resolving: false,
    };
  }, [saved, needsName, settled, employeeId, retry]);
};

export default useWorkflowPifResumedIdentity;
